import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { X, Zap } from "lucide-react";
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceArea,
  ResponsiveContainer,
} from "recharts";
import {
  SEVERITY_BANDS,
  SEVERITY_COLORS,
  SEVERITY_TEXT,
  SEVERITY_LEVELS,
  rangeStart,
} from "utils/caseLiveStats";
import { fmtTime } from "../parts";
import { Avatar, Segmented, SeverityLegend, gText, useAgo } from "./shared";

const hhmm = (v) =>
  new Date(v).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

const BAND_TINTS = ["#FEFCE8", "#FFF7ED", "#FEF2F2"]; // moderate, hard, severe

/** Impacts over time, as dots by size; click one to follow that player. */
export const ImpactTimeline = ({ stats, replay, selected, onSelect }) => {
  const { t } = useTranslation();
  const [range, setRange] = useState(replay ? "all" : "15m");
  const [colorBy, setColorBy] = useState("severity");
  const from = rangeStart(range, stats);
  const to = stats.end;
  const latestId = stats.last
    ? stats.points[stats.points.length - 1]?.id
    : null;

  const data = useMemo(
    () => stats.points.filter((p) => p.t >= from && p.t <= to),
    [stats.points, from, to]
  );
  const maxG = Math.max(60, ...data.map((p) => p.g));
  const yMax = Math.ceil((maxG * 1.08) / 20) * 20;

  const ranges = [
    { value: "5m", label: t("caseDashboard.live.range.5m") },
    { value: "15m", label: t("caseDashboard.live.range.15m") },
    { value: "1h", label: t("caseDashboard.live.range.1h") },
    { value: "all", label: t("caseDashboard.live.range.all") },
  ];

  const dot = (props) => {
    const { cx, cy, payload } = props;
    if (cx == null || cy == null) return null;
    const color =
      colorBy === "severity" ? SEVERITY_COLORS[payload.sev] : payload.color;
    const dim = selected && payload.key !== selected;
    const r = 3.5 + payload.sev * 1.3;
    const isLatest = !replay && payload.id === latestId;
    return (
      <g style={{ cursor: "pointer" }} onClick={() => onSelect(payload.key)}>
        {isLatest && (
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke={color}
            strokeWidth={2}
          >
            <animate
              attributeName="r"
              from={r}
              to={r + 12}
              dur="1.6s"
              repeatCount="indefinite"
            />
            <animate
              attributeName="opacity"
              from="0.8"
              to="0"
              dur="1.6s"
              repeatCount="indefinite"
            />
          </circle>
        )}
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill={color}
          fillOpacity={dim ? 0.12 : 0.85}
          stroke="#fff"
          strokeWidth={dim ? 0 : 1}
        />
      </g>
    );
  };

  return (
    <div className="rounded-xl border border-[#F3F4F6] bg-white">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#F3F4F6] px-4 py-3">
        <h2 className="text-sm font-semibold text-[#111827]">
          {t("caseDashboard.live.timeline")}
          <span className="ml-2 text-[12px] font-normal text-[#9CA3AF]">
            {t("caseDashboard.live.inView", { count: data.length })}
          </span>
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          <Segmented
            value={colorBy}
            onChange={setColorBy}
            options={[
              {
                value: "severity",
                label: t("caseDashboard.live.colorBy.severity"),
              },
              {
                value: "player",
                label: t("caseDashboard.live.colorBy.player"),
              },
            ]}
          />
          <Segmented value={range} onChange={setRange} options={ranges} />
        </div>
      </div>
      {stats.points.length === 0 ? (
        <EmptyChart />
      ) : (
        <div className="h-[300px] px-2 pb-2 pt-4">
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 4, right: 16, bottom: 4, left: 0 }}>
              {SEVERITY_BANDS.map((lo, i) => (
                <ReferenceArea
                  key={lo}
                  y1={lo}
                  y2={SEVERITY_BANDS[i + 1] ?? yMax}
                  fill={BAND_TINTS[i]}
                  fillOpacity={0.7}
                  ifOverflow="hidden"
                />
              ))}
              <CartesianGrid stroke="#F3F4F6" vertical={false} />
              <XAxis
                dataKey="t"
                type="number"
                domain={[from, to]}
                allowDataOverflow
                tickFormatter={(v) =>
                  to - from > 10 * 60e3 ? hhmm(v) : fmtTime(new Date(v))
                }
                tick={{ fontSize: 11, fill: "#9CA3AF" }}
                tickLine={false}
                axisLine={{ stroke: "#E5E7EB" }}
                minTickGap={40}
              />
              <YAxis
                dataKey="g"
                type="number"
                domain={[0, yMax]}
                ticks={[
                  0,
                  ...SEVERITY_BANDS,
                  ...(yMax > 100 ? [100] : []),
                  yMax,
                ].filter((v, i, a) => a.indexOf(v) === i && v <= yMax)}
                tick={{ fontSize: 11, fill: "#9CA3AF" }}
                tickLine={false}
                axisLine={false}
                unit=" g"
                width={52}
              />
              <Tooltip
                cursor={{ strokeDasharray: "3 3", stroke: "#D1D5DB" }}
                content={<PointTooltip />}
                isAnimationActive={false}
              />
              <Scatter data={data} shape={dot} isAnimationActive={false} />
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-[#F3F4F6] px-4 py-2.5">
        {colorBy === "severity" ? (
          <SeverityLegend counts={stats.severity} />
        ) : (
          <div className="flex flex-wrap gap-x-3 gap-y-1">
            {stats.active.map((p) => (
              <button
                type="button"
                key={p.key}
                onClick={() => onSelect(p.key)}
                className={`flex items-center gap-1.5 text-[11px] transition duration-150 ${
                  selected && selected !== p.key
                    ? "text-[#D1D5DB]"
                    : "text-[#6B7280] hover:text-[#111827]"
                }`}
              >
                <span
                  className="h-2 w-2 rounded-full"
                  style={{ backgroundColor: p.color }}
                />
                {p.name}
              </button>
            ))}
          </div>
        )}
        <span className="text-[11px] text-[#9CA3AF]">
          {t("caseDashboard.live.clickHint")}
        </span>
      </div>
    </div>
  );
};

const PointTooltip = ({ active, payload }) => {
  const { t } = useTranslation();
  const p = active && payload?.[0]?.payload;
  if (!p) return null;
  return (
    <div className="rounded-lg border border-[#F3F4F6] bg-white px-3 py-2 shadow-[0_4px_16px_rgba(0,0,0,0.08)]">
      <div className="flex items-center gap-2">
        <span
          className="h-2 w-2 rounded-full"
          style={{ backgroundColor: p.color }}
        />
        <span className="text-[13px] font-semibold text-[#111827]">
          {p.name || "—"}
        </span>
      </div>
      <div className="mt-1 flex items-baseline gap-2">
        <span
          className="text-base font-bold"
          style={{ color: SEVERITY_TEXT[p.sev] }}
        >
          {gText(t, p.g)}
        </span>
        <span className="text-[11px] text-[#6B7280]">
          {t(`caseDashboard.live.severity.${SEVERITY_LEVELS[p.sev]}`)} ·{" "}
          {fmtTime(new Date(p.t))}
        </span>
      </div>
    </div>
  );
};

const EmptyChart = () => {
  const { t } = useTranslation();
  return (
    <div className="flex h-[300px] flex-col items-center justify-center text-center">
      <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-[#F3F4F6]">
        <Zap size={22} className="text-[#9CA3AF]" />
      </div>
      <p className="text-sm font-semibold text-[#111827]">
        {t("caseDashboard.live.noImpacts")}
      </p>
      <p className="mt-1 max-w-xs text-[13px] text-[#6B7280]">
        {t("caseDashboard.live.noImpactsHint")}
      </p>
    </div>
  );
};

/** Newest hits first; new ones slide in. Follows the selected player. */
export const ImpactFeed = ({
  stats,
  replay,
  now,
  selected,
  onSelect,
  onClear,
}) => {
  const { t } = useTranslation();
  const ago = useAgo();
  const seen = useRef(null);
  const playerName =
    selected && stats.players.find((p) => p.key === selected)?.name;
  const items = useMemo(() => {
    const list = selected
      ? stats.points.filter((p) => p.key === selected)
      : stats.points;
    return list.slice(-40).reverse();
  }, [stats.points, selected]);
  // Ids present on first render don't animate; later arrivals do.
  if (seen.current === null)
    seen.current = new Set(stats.points.map((p) => p.id));

  return (
    <div className="flex h-full flex-col rounded-xl border border-[#F3F4F6] bg-white">
      <div className="flex items-center justify-between border-b border-[#F3F4F6] px-4 py-3">
        <h2 className="text-sm font-semibold text-[#111827]">
          {t("caseDashboard.live.latest")}
        </h2>
        {selected && (
          <button
            type="button"
            onClick={onClear}
            className="flex items-center gap-1 rounded-full bg-[#F3F4F6] px-2 py-0.5 text-[11px] font-medium text-[#374151] transition duration-150 hover:bg-[#E5E7EB]"
          >
            {playerName}
            <X size={11} />
          </button>
        )}
      </div>
      <div className="max-h-[360px] min-h-[200px] flex-1 overflow-y-auto">
        {items.length === 0 ? (
          <p className="px-4 py-10 text-center text-[13px] text-[#9CA3AF]">
            {t("caseDashboard.live.noImpacts")}
          </p>
        ) : (
          <AnimatePresence initial={false}>
            {items.map((p) => {
              const fresh = !seen.current.has(p.id);
              return (
                <motion.button
                  type="button"
                  layout="position"
                  key={p.id}
                  onClick={() => onSelect(p.key)}
                  initial={
                    fresh
                      ? { opacity: 0, x: -12, backgroundColor: "#FEF3E7" }
                      : false
                  }
                  animate={{ opacity: 1, x: 0, backgroundColor: "#FFFFFF" }}
                  transition={{
                    duration: 0.35,
                    backgroundColor: { duration: 2.5 },
                  }}
                  className="flex w-full items-center gap-3 border-b border-[#F9FAFB] px-4 py-2.5 text-left transition-colors hover:bg-[#FAFAFA]"
                >
                  <span
                    className="h-8 w-1 shrink-0 rounded-full"
                    style={{ backgroundColor: SEVERITY_COLORS[p.sev] }}
                  />
                  <Avatar name={p.name} color={p.color} size={26} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-medium text-[#111827]">
                      {p.name || "—"}
                    </span>
                    <span className="block text-[11px] text-[#9CA3AF]">
                      {replay ? fmtTime(new Date(p.t)) : ago(p.t, now)}
                    </span>
                  </span>
                  <span className="text-right">
                    <span
                      className="block text-sm font-bold tabular-nums"
                      style={{ color: SEVERITY_TEXT[p.sev] }}
                    >
                      {gText(t, p.g)}
                    </span>
                    <span className="block text-[10px] font-medium uppercase tracking-wide text-[#9CA3AF]">
                      {t(
                        `caseDashboard.live.severity.${SEVERITY_LEVELS[p.sev]}`
                      )}
                    </span>
                  </span>
                </motion.button>
              );
            })}
          </AnimatePresence>
        )}
      </div>
    </div>
  );
};
